<script setup lang="ts">
import { useId } from 'vue'
import {
  KsRadioGroupItem,
  KsRadioGroupIndicator,
  type KsRadioGroupItemProps,
} from '@aschehoug/kloss-2'

export type RadioGroupButtonProps = Omit<KsRadioGroupItemProps, 'id'> & {
  label?: string
}

const { label, ...props } = defineProps<RadioGroupButtonProps>()

const id = useId()
</script>

<template>
  <div>
    <KsRadioGroupItem
      :id="id"
      class="radio-group-item"
      v-bind="props"
    >
      <KsRadioGroupIndicator class="radio-group-indicator" />
    </KsRadioGroupItem>
    <slot name="label">
      <CommonTypography
        component="label"
        variant="body"
        :for="id"
      >
        {{ label }}
      </CommonTypography>
    </slot>
  </div>
</template>

<style lang="scss" scoped>
  div {
    display: flex;
    align-items: center;
  }

  button {
    all: unset;
  }

  label {
    padding-left: 15px;
  }

  .radio-group-item {
    width: 22px;
    height: 22px;
    border-radius: 100%;
    border: 2px solid var(--color-gray-30);
    background-color: var(--color-white);
    flex-shrink: 0;
  }

  .radio-group-item:hover {
    cursor: pointer;
    border-color: var(--color-green-60);
  }

  .radio-group-item:focus {
    @include visible-focus-ring;
  }

  .radio-group-item[aria-checked='true'] {
    border-color: var(--color-seagreen-40);
    background-color: var(--color-seagreen-40);
  }

  .radio-group-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .radio-group-indicator::after {
    content: '';
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: var(--color-white);
  }

  .radio-group-item[aria-checked='true']:hover {
    border-color: var(--color-green-60);
    background-color: var(--color-white);

    .radio-group-indicator::after {
      background-color: var(--color-green-60);
    }
  }
</style>
